import React from 'react';
import { Box, Typography, ButtonBase, Tooltip } from '@mui/material';
import { appColors } from '../../Config/Theme';

export interface CompactMetricBoxProps {
  label: string;
  value: number | string;
  ariaLabel?: string;
  tooltip?: string;
  onClick?: () => void;
  selected?: boolean;
}

/**
 * A compact, no-icon metric box that shows a label on the left and a number on
 * the right.  Used for secondary / context metrics (Full Roster, No Badge Today)
 * that sit alongside the primary KpiCards in the same grid row.
 */
const CompactMetricBox: React.FC<CompactMetricBoxProps> = ({
  label,
  value,
  ariaLabel,
  tooltip,
  onClick,
  selected = false,
}) => {
  const isClickable = !!onClick;

  const boxSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: 0,
    height: { xs: 72, lg: 80 },
    px: { xs: 1.5, lg: 2 },
    py: { xs: 1, lg: 1.25 },
    bgcolor: selected ? `${appColors.primary.main}08` : appColors.surface.card,
    borderRadius: '12px',
    border: selected
      ? `2px solid ${appColors.primary.main}`
      : `1px solid ${appColors.border.default}`,
    boxShadow: selected ? `0 0 0 3px ${appColors.primary.main}20` : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
    boxSizing: 'border-box' as const,
  };

  const inner = (
    <>
      <Typography
        sx={{
          fontSize: { xs: '1.25rem', lg: '1.4rem' },
          fontWeight: 700,
          color: appColors.text.heading,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
        }}
      >
        {value ?? '—'}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: '0.78rem', lg: '0.84rem' },
          fontWeight: 600,
          color: appColors.primary.dark,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
    </>
  );

  const resolvedAriaLabel = ariaLabel || `${label}, ${value}`;

  const wrap = (node: React.ReactElement) =>
    tooltip ? <Tooltip title={tooltip} arrow>{node}</Tooltip> : node;

  if (isClickable) {
    return wrap(
      <ButtonBase
        onClick={onClick}
        aria-label={resolvedAriaLabel}
        aria-pressed={selected}
        data-compact-metric
        sx={{
          ...boxSx,
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': {
            borderColor: selected ? appColors.primary.main : appColors.border.hover,
            boxShadow: selected
              ? `0 0 0 3px ${appColors.primary.main}20`
              : appColors.shadow?.cardHover ?? 'none',
          },
          '&:focus-visible': {
            outline: `2px solid ${appColors.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        {inner}
      </ButtonBase>,
    );
  }

  return wrap(
    <Box
      role="group"
      aria-label={resolvedAriaLabel}
      data-compact-metric
      sx={boxSx}
    >
      {inner}
    </Box>,
  );
};

export default CompactMetricBox;
